import isEqual from "lodash/isEqual";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { StringParam, useQueryParams } from "use-query-params";
import { useOnClickOutside } from "../../util/hooks";
import { usePortalPopoverPosition } from "../../util/hooks/usePortalPopoverPosition";
import { usePopoverFocus } from "../../util/hooks/usePopoverFocus";
import type { IIsOutOfView } from "../../util/util";
import { isOutOfViewport, useMediaQueries } from "../../util/util";
import { TertiaryButtonSmall } from "../Buttons/Buttons";
import type { ChipType } from "../Chips/Chips";
import { Modal } from "../Modal/Modal";
import { PrefilledSelectAllChips } from "../PrefilledSelectAllChips/PrefilledSelectAllChips";
import { DropDownModalWrapper, DropDownWrapper } from "./filter_by.utils";
import ReactDOM from "react-dom";
import styled from "styled-components/macro";

type FilterButtonProps = React.ComponentProps<typeof TertiaryButtonSmall> & {
  size?: "small" | "default";
};

export const FilterButton = styled(TertiaryButtonSmall)<FilterButtonProps>`
  ${({ size, theme }) =>
    size === "small" &&
    `
      padding: ${theme.primitiveTokens.spacing.spacing_xs}
               ${theme.primitiveTokens.spacing.spacing_md};
      border-radius: ${theme.primitiveTokens.radii.radius_xs};
      font-size: ${theme.fontSizes.font_size_sm};
      font-weight: ${theme.fontWeights.font_weight_medium};
    `}

  ${({ size, theme, active }) =>
    size === "small" &&
    !active &&
    `
      && {
        border: 1px solid ${theme.secondaryBorder};
      }
    `}
`;

// Must stay in sync with `DropDownModalWrapper`'s width.
const DROPDOWN_WIDTH = 500;

interface IFilterBy {
  setter: ({ values, filter }: { values: ChipType[]; filter: string }) => void;
  options: ChipType[];
  header: string;
  filter: string;
  label: string;
  size?: "small" | "default";
  preselectedFilters: ChipType[];
  isLastVisible?: boolean;
  usePortal?: boolean;
}

/**
 * Reads in filters from the URL if they exist and applies them to the chips.
 * selectedFilters will update when a user clicks on a chip, but they will only be
 * applied if "apply" or "clear" are clicked. When that array changes the request will run.
 */
export const FilterBy = ({
  setter,
  options,
  header,
  filter,
  label,
  preselectedFilters,
  isLastVisible,
  size = "default",
  usePortal = false,
}: IFilterBy) => {
  const [showDropDown, setShowDropDown] = useState(false);
  const [openedFromKeyboard, setOpenedFromKeyboard] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<ChipType[]>(
    preselectedFilters || []
  );
  const [appliedFilters, setAppliedFilters] = useState<ChipType[]>(
    preselectedFilters || []
  );
  const [showModal, setShowModal] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [query, setQuery] = useQueryParams({
    active_tag_view: StringParam,
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [ButtonViewport, setButtonViewport] = useState<
    IIsOutOfView | undefined
  >(isOutOfViewport(buttonRef));

  const handleFilterClick = (activeChips: ChipType[]) => {
    setSelectedFilters([...activeChips]);
  };

  const getButtonText = useCallback(() => {
    const optionNamesSet = new Set(options.map((opt) => opt.name));
    const validAppliedFilters = appliedFilters.filter((filter) =>
      optionNamesSet.has(filter.name)
    );

    if (validAppliedFilters.length === 0) {
      // Selection exists but no matching options (e.g. no data for that filter)
      return appliedFilters.length > 0 ? `${label} (0)` : `${label} `;
    }
    return `${label} (${validAppliedFilters.length})`;
  }, [appliedFilters, label, options]);
  const [buttonText, setButtonText] = useState(getButtonText);

  const handleClickOutside = () => {
    setButtonText(getButtonText());

    if (!isEqual(appliedFilters, selectedFilters)) {
      // When the user clicks outside the modal without applying or clearing
      // and there are changes present
      // reset the selected filters with the applied filters.
      setSelectedFilters(appliedFilters);
      setButtonText(getButtonText());
    }
    setShowDropDown(false);
  };

  useEffect(() => {
    setButtonText(getButtonText());
  }, [appliedFilters, getButtonText]);

  // Presses on the trigger are left to its own toggle, otherwise mousedown
  // would close the dropdown and the following click would reopen it.
  useOnClickOutside(wrapperRef, (event: Event) => {
    if (
      event.target instanceof Node &&
      buttonRef.current?.contains(event.target)
    ) {
      return;
    }
    handleClickOutside();
  });

  const { restoreFocusOnClose, onPopoverKeyDown } = usePopoverFocus({
    isOpen: showDropDown && usePortal,
    triggerRef: buttonRef,
    popoverRef: wrapperRef,
    onClose: handleClickOutside,
    openedFromKeyboard,
    initialFocusSelector: "input",
  });

  const apply = () => {
    setAppliedFilters(selectedFilters);
    setter({ values: selectedFilters, filter: filter });
    setButtonText(getButtonText());
    restoreFocusOnClose();
    setShowDropDown(false);
  };

  const clear = useCallback(() => {
    // useCallback is needed here because this a dep of the
    // below useEffect
    setSelectedFilters([]);
    setAppliedFilters([]);
    setQuery({
      [filter]: query.active_tag_view,
    });
    setter({ values: [], filter: filter });
    restoreFocusOnClose();
    setShowDropDown(false);
  }, [setter, filter, query.active_tag_view, setQuery, restoreFocusOnClose]);

  useEffect(() => {
    setSelectedFilters(preselectedFilters);
    setAppliedFilters(preselectedFilters);
  }, [preselectedFilters]);

  const portalStyle = usePortalPopoverPosition({
    isOpen: showDropDown && usePortal,
    triggerRef: buttonRef,
    popoverRef: wrapperRef,
    popoverWidth: DROPDOWN_WIDTH,
    onTriggerHidden: handleClickOutside,
  });

  React.useEffect(() => {
    const handleResize = () => setButtonViewport(isOutOfViewport(buttonRef));
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const { isMediumScreen } = useMediaQueries();
  if (!isMediumScreen) {
    return (
      <DropDownWrapper size={size}>
        <FilterButton
          size={size}
          active={appliedFilters.length > 0 || showDropDown}
          ref={buttonRef}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            if (showDropDown) {
              // Same discard-unapplied-selection close as an outside click.
              restoreFocusOnClose();
              handleClickOutside();
              return;
            }
            setOpenedFromKeyboard(event.detail === 0);
            setShowDropDown(true);
            setButtonViewport(isOutOfViewport(buttonRef));
          }}
          className="button-item"
          style={{
            opacity: isLastVisible ? "0" : "1",
            pointerEvents: isLastVisible ? "none" : "auto",
          }}
        >
          {buttonText}
        </FilterButton>
        {showDropDown &&
          (usePortal ? (
            ReactDOM.createPortal(
              <DropDownModalWrapper
                ref={wrapperRef}
                className="dropdownWrapperDiv"
                isOutOfViewport={ButtonViewport}
                style={portalStyle}
                tabIndex={-1}
                onKeyDown={onPopoverKeyDown}
              >
                {/* Single content child, measured by usePortalPopoverPosition. */}
                <div>
                  <PrefilledSelectAllChips
                    allChips={options}
                    handleClick={handleFilterClick}
                    header={header}
                    withSelectAll={true}
                    selectedChips={selectedFilters}
                    ToggleLabel={label}
                    handleApply={apply}
                    handleClear={clear}
                    scroll={true}
                    idPrefix={filter}
                  />
                </div>
              </DropDownModalWrapper>,
              document.getElementById("filter-dropdown-portal") ?? document.body
            )
          ) : (
            <DropDownModalWrapper
              ref={wrapperRef}
              className="dropdownWrapperDiv"
              isOutOfViewport={ButtonViewport}
            >
              <PrefilledSelectAllChips
                allChips={options}
                handleClick={handleFilterClick}
                header={header}
                withSelectAll={true}
                selectedChips={selectedFilters}
                ToggleLabel={label}
                handleApply={apply}
                handleClear={clear}
                scroll={true}
                idPrefix={filter}
              />
            </DropDownModalWrapper>
          ))}
      </DropDownWrapper>
    );
  } else {
    return (
      <DropDownWrapper size={size}>
        <FilterButton
          active={appliedFilters.length > 0}
          onClick={() => {
            setShowModal(true);
          }}
          size={size}
        >
          {buttonText}
        </FilterButton>
        <Modal show={showModal} closeModal={() => setShowModal(false)}>
          <PrefilledSelectAllChips
            allChips={options}
            handleClick={handleFilterClick}
            header={header}
            withSelectAll={true}
            selectedChips={selectedFilters}
            ToggleLabel={label}
            handleApply={() => {
              apply();
              setShowModal(false);
            }}
            handleClear={() => {
              clear();
              setShowModal(false);
            }}
            idPrefix={filter}
          />
        </Modal>
      </DropDownWrapper>
    );
  }
};
